/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { addClass, hasClass } from '@grapecity/wijmo';
import { DataMap, CellType, CellRange, HeadersVisibility } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useLoading } from 'components/process/Loading';
import { commonYNList } from 'models/common/CommonCode';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import {
  GlobalBtnGroup,
  ControlBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { CrudCode } from 'models/common/Edit';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { CommonUtil } from 'utils/CommonUtil';
import CustomGrid, { CustomMergeManager } from 'components/grids/CustomGrid';
import { hasRole } from 'utils/SessionUtil';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { findSetupStep, saveSetupStep } from 'apis/setup/SetupStep';
import { SetupStep, SetupStepCondition } from 'models/setup/SetupStep';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 셋업 단계 등록/조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.06.25   jshwang12       신규 생성
 */
const SetupStepRegistPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openLoading, isLoading } = useLoading();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const initCondition = {
    istlDivsCds: [],
    prjSpNm: '',
  };
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<SetupStepCondition>({ ...initCondition });
  const [rowData, setRowData] = useState<SetupStep[]>([]);
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  useEffect(() => {
    getCommonCodes();
    // [26.08.06] FA 관리자만 수정 가능
    if (hasRole('ADM') || hasRole('FA_MANAGER')) {
      setHasAuth(true);
    }
    handleSearch();
  }, []);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('PRJ_SP_DIVS_CD'), // 셋업구분
    ]).then(([prjSpDivsCd]) => {
      setCode({
        prjSpDivsCd: prjSpDivsCd,
      });
    });
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearch = () => {
    findSetupStep(condition).then((result) =>
      setRowData(
        (result || []).reduce(
          (acc, cur) => [...acc, { ...cur, prjSpCdDisplay: cur.prjSpCd }],
          [] as SetupStep[]
        )
      )
    );
  };

  const validate = () => {
    const seen = new Map<string, number>();
    const dupKeys = [] as string[];
    (rowData || []).forEach((item) => {
      const prjSpCd =
        item.crudKey === CrudCode.CREATE ? `${item.prjSpCdDisplay}${item.prjSpCd}` : item.prjSpCd;
      if (prjSpCd) {
        const count = seen.get(prjSpCd) || 0;
        seen.set(prjSpCd, count + 1);
        if (count === 1) {
          dupKeys.push(prjSpCd);
        }
      }
    });
    const valid = (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .map((o, index) => {
        if (_.isEmpty(o.istlDivsCd)) {
          return `${index + 1} : ${t(
            'setup.label.셋업 구분을 입력해 주세요.',
            '셋업 구분을 입력해 주세요.'
          )}`;
        }
        if (_.isEmpty(o.prjSpCd)) {
          return `${index + 1} : ${t(
            'setup.label.셋업 단계 코드를 입력해 주세요.',
            '셋업 단계 코드를 입력해 주세요.'
          )}`;
        }
        const prjSpCd =
          o.crudKey === CrudCode.CREATE ? `${o.prjSpCdDisplay}${o.prjSpCd}` : o.prjSpCd;
        if (dupKeys.includes(prjSpCd || '')) {
          return `${index + 1} : ${t(
            'setup.label.중복된 셋업 단계 코드가 있습니다.',
            '중복된 셋업 단계 코드가 있습니다.'
          )} (${prjSpCd})`;
        }
        if (_.isEmpty(o.prjSpNm)) {
          return `${index + 1} : ${t(
            'setup.label.셋업 단계 명을 입력해 주세요.',
            '셋업 단계 명을 입력해 주세요.'
          )}`;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    const rows = (rowData || []).filter((o) => !!o.crudKey);
    if (rows.length) {
      if (validate()) {
        openCommonModal({
          modalType: 'confirm',
          content: t('com.button.저장하시겠습니까?', '저장하시겠습니까?'),
          yesCallback: async () => {
            openLoading(true);
            saveSetupStep(rows)
              .then((res) => {
                openMessageBar({
                  type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
                  content:
                    res?.successOrNot === 'Y'
                      ? t('com.label.저장되었습니다.', '저장되었습니다.')
                      : res.data && typeof res.data === 'string'
                      ? res.data
                      : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
                });
                if (res?.successOrNot === 'Y') {
                  handleSearch();
                }
              })
              .finally(() => openLoading(false));
          },
        });
      }
    }
  };

  const handleAddRow = () => {
    const newData = {
      crudKey: CrudCode.CREATE,
      istlDivsCd: '',
      prjSpCdDisplay: '',
      prjSpCd: '',
    };
    setRowData((prev) => [newData, ...prev]);
  };

  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);

    if ((selectedRows || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('com.label.삭제할 데이터를 선택해 주세요.', '삭제할 데이터를 선택해 주세요.'),
      });
      return;
    }

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.셋업 단계 목록', '셋업 단계 목록'),
      t('setup.label.셋업 단계 목록', '셋업 단계 목록')
    );
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if ('prjSpCdDisplay' === binding) {
        cell.ariaReadOnly = true;
      } else if (['prjSpCd', 'istlDivsCd'].includes(binding)) {
        // 수정 시 키값 수정불가
        const readOnly = CrudCode.CREATE !== item.crudKey;
        cell.ariaReadOnly = readOnly;
        if (readOnly) {
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
      // 사용여부
      else if ('useYn' === binding) {
        if ('Y' === item.usedYn) {
          cell.ariaReadOnly = true;
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const onBeginningEdit = useEvent((grid, e) => {
    if (!hasAuth) {
      e.cancel = true;
    } else {
      const col = grid.columns[e.col];
      const binding = col.binding;
      const item = grid.rows[e.row].dataItem;

      if ('prjSpCdDisplay' === binding) {
        e.cancel = true;
      }
      // 수정 시 키값 수정불가
      if (['prjSpCd', 'istlDivsCd'].includes(binding)) {
        if (CrudCode.CREATE !== item.crudKey) {
          e.cancel = true;
        }
      }
      // 사용여부
      else if ('useYn' === binding) {
        if ('Y' === item.usedYn) {
          e.cancel = true;
        }
      }
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;

    // 셋업구분
    if ('istlDivsCd' === binding) {
      const newPrefix =
        (code?.prjSpDivsCd || []).find((o) => o.cmnCdNm === newVal)?.optValCtn1 || '';
      item.prjSpCdDisplay = newPrefix;
    }
    // 셋업 단계 코드
    if ('prjSpCd' === binding) {
      // 숫자만 입력 가능
      if (newVal) {
        if (!/^[0-9]+$/.test(newVal)) {
          e.cancel = true;
          openMessageBar({
            type: 'error',
            content: t(
              'setup.label.단계코드는 숫자 3자리로 입력해 주세요.',
              '단계코드는 숫자 3자리로 입력해 주세요.'
            ),
          });
          return;
        }
        // 3자리가 아닌 경우
        if (newVal.length !== 3) {
          e.cancel = true;
          openMessageBar({
            type: 'error',
            content: t(
              'setup.label.단계코드는 숫자 3자리로 입력해 주세요.',
              '단계코드는 숫자 3자리로 입력해 주세요.'
            ),
          });
          return;
        }
      }
    }
  });

  const onCellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
    grid.headersVisibility = !hasAuth ? HeadersVisibility.Column : HeadersVisibility.All;
    grid.mergeManager = new ProjectMergeManager();

    grid.formatItem.addHandler((s, e) => {
      const item = s.rows[e.row]?.dataItem;
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          if (item?.usedYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.usedYn === 'Y') {
              row.isSelected = false;
            }
          });
        });
      }
    });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        visible: hasAuth,
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'istlDivsCd',
        header: String(t('setup.label.셋업 구분', '셋업 구분')),
        align: 'center',
        minWidth: 80,
        width: 100,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.prjSpDivsCd || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'prjSpCdDisplay',
        header: String(t('setup.label.셋업 단계 코드', '셋업 단계 코드')),
        align: 'center',
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        allowMerging: true,
      },
      {
        binding: 'prjSpCd',
        header: String(t('setup.label.셋업 단계 코드', '셋업 단계 코드')),
        align: 'center',
        minWidth: 50,
        width: 90,
        allowMerging: true,
      },
      {
        binding: 'prjSpNm',
        header: String(t('setup.label.셋업 단계 명', '셋업 단계 명')),
        align: 'center',
        minWidth: 120,
        width: '*',
      },
      {
        binding: 'useYn',
        header: String(t('com.label.사용여부', '사용여부')),
        align: 'center',
        minWidth: 70,
        width: 100,
        dataMap: new DataMap(commonYNList, 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        align: 'center',
        minWidth: 40,
        width: 120,
        isReadOnly: true,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        align: 'center',
        minWidth: 40,
        width: 120,
        isReadOnly: true,
      },
    ];
  }, [code, hasAuth]);
  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>
                  <span>{String(t('setup.label.셋업 구분', '셋업 구분'))}</span>
                </label>
                <MultiComboBox
                  id="istlDivsCds"
                  options={code?.prjSpDivsCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.istlDivsCds || []}
                  onChange={(value) => handleOnChange('istlDivsCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('setup.label.셋업 단계', '셋업 단계'))}</span>
                </label>
                <CustomInputWithSearch
                  name="prjSpNm"
                  placeholder={String(
                    t('setup.label.셋업 단계를 입력해 주세요.', '셋업 단계를 입력해 주세요.')
                  )}
                  value={condition?.prjSpNm}
                  onChange={(e) => {
                    handleOnChange(e.target.name, e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={handleSearch}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.셋업 단계 목록', '셋업 단계 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.button.행추가', '행추가')}
              </Button>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleDelRow}
                disableRipple
              >
                {t('com.button.행삭제', '행삭제')}
              </Button>
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={580}
        isReadOnly={!hasAuth}
        beginningEdit={onBeginningEdit}
        cellEditEnding={onCellEditEnding}
        cellEditEnded={onCellEditEnded}
        initialized={onInitialized}
      />
      {hasAuth && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={handleSave}>
            {t('com.button.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

class ProjectMergeManager extends CustomMergeManager {
  getMergedRange(panel, r, c, clip = true) {
    if (panel.cellType === CellType.Cell) {
      const item = panel.grid.rows[r].dataItem;
      if (item.crudKey === CrudCode.CREATE) {
        return new CellRange(r, c);
      }
    }
    return super.getMergedRange(panel, r, c, clip);
  }
}

export default SetupStepRegistPage;
